import { useEffect, useRef, useState } from 'react'
import { useStytchB2BClient } from '@stytch/react/b2b'
import { router } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import MarketingLayout from '@/layouts/MarketingLayout'

type Phase = 'authenticating' | 'select_org' | 'create_org' | 'exchanging' | 'error'

interface DiscoveredOrg {
  organization_id: string
  organization_name: string
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function Authenticate() {
  const stytch = useStytchB2BClient()
  const ran = useRef(false)

  const [phase, setPhase] = useState<Phase>('authenticating')
  const [error, setError] = useState<string | null>(null)
  const [orgName, setOrgName] = useState('')
  const [website, setWebsite] = useState('')
  const [websiteError, setWebsiteError] = useState<string | null>(null)
  const [fullName, setFullName] = useState('')
  const [discoveredOrgs, setDiscoveredOrgs] = useState<DiscoveredOrg[]>([])

  // Phase 1: Validate discovery token
  useEffect(() => {
    if (ran.current) return
    ran.current = true

    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    const tokenType = params.get('stytch_token_type')

    if (!token || tokenType !== 'discovery') {
      setError('Invalid authentication link.')
      setPhase('error')
      return
    }

    const authenticate = async () => {
      try {
        const resp = await stytch.magicLinks.discovery.authenticate({
          discovery_magic_links_token: token,
        })

        const orgs = resp.discovered_organizations ?? []

        if (orgs.length === 1) {
          // Single org — exchange immediately
          const orgId = orgs[0].organization?.organization_id
          if (orgId) {
            setPhase('exchanging')
            await stytch.discovery.intermediateSessions.exchange({
              organization_id: orgId,
              session_duration_minutes: 525600,
            })
            router.post('/auth/callback')
            return
          }
        }

        if (orgs.length > 1) {
          // Multiple orgs — let user choose
          setDiscoveredOrgs(
            orgs
              .filter((o) => o.organization?.organization_id)
              .map((o) => ({
                organization_id: o.organization!.organization_id,
                organization_name: o.organization!.organization_name,
              }))
          )
          setPhase('select_org')
          return
        }

        // No orgs — show create form, pre-fill from email in discovery response
        const email = (resp as any).email_address as string | undefined
        if (email) {
          const domain = email.split('@')[1]?.split('.')[0]
          if (domain) setOrgName(domain.charAt(0).toUpperCase() + domain.slice(1))
        }

        setPhase('create_org')
      } catch (err: any) {
        console.error('Stytch authentication error:', err)
        setError('Authentication failed. Please try again.')
        setPhase('error')
      }
    }

    authenticate()
  }, [stytch])

  const handleSelectOrg = async (orgId: string) => {
    setPhase('exchanging')
    try {
      await stytch.discovery.intermediateSessions.exchange({
        organization_id: orgId,
        session_duration_minutes: 525600,
      })
      router.post('/auth/callback')
    } catch (err: any) {
      console.error('Org exchange error:', err)
      setError('Failed to join organization. Please try again.')
      setPhase('error')
    }
  }

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgName.trim() || !fullName.trim()) return

    const trimmedWebsite = website.trim()
    if (trimmedWebsite && !/^https?:\/\/.+\..+$/i.test(trimmedWebsite)) {
      setWebsiteError('Must be a valid URL starting with http:// or https://')
      return
    }

    setPhase('exchanging')

    try {
      await stytch.discovery.organizations.create({
        organization_name: orgName.trim(),
        organization_slug: slugify(orgName),
        session_duration_minutes: 525600,
      })

      router.post('/auth/callback', { full_name: fullName.trim(), website: website.trim() })
    } catch (err: any) {
      console.error('Org creation error:', err)
      setError('Failed to create organization. Please try again.')
      setPhase('error')
    }
  }

  if (phase === 'error') {
    return (
      <div className="mx-auto max-w-sm pt-12 text-center">
        <p className="text-red-600">{error}</p>
        <a href="/login" className="mt-4 inline-block text-sm text-primary hover:underline">
          Back to Sign In
        </a>
      </div>
    )
  }

  if (phase === 'select_org') {
    return (
      <div className="mx-auto max-w-sm pt-12">
        <Card>
          <CardHeader>
            <CardTitle>Select Organization</CardTitle>
            <CardDescription>
              Choose which organization to sign into.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {discoveredOrgs.map((org) => (
              <Button
                key={org.organization_id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleSelectOrg(org.organization_id)}
              >
                {org.organization_name}
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (phase === 'create_org') {
    return (
      <div className="mx-auto max-w-sm pt-12">
        <Card>
          <CardHeader>
            <CardTitle>Create Your Company</CardTitle>
            <CardDescription>
              Enter your company details to get started.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateOrg} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Company Name</Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Acme Health"
                  autoFocus
                  required
                />
                {orgName && (
                  <p className="text-xs text-muted-foreground">
                    Slug: {slugify(orgName) || '...'}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Company Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={website}
                  onChange={(e) => { setWebsite(e.target.value); setWebsiteError(null) }}
                  placeholder="https://acmehealth.com"
                />
                {websiteError && (
                  <p className="text-xs text-destructive">{websiteError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="full-name">Your Name</Label>
                <Input
                  id="full-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Doe"
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                Continue
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    )
  }

  // authenticating or exchanging
  return (
    <div className="mx-auto max-w-sm pt-12 text-center">
      <p className="text-muted-foreground">
        {phase === 'exchanging' ? 'Setting up your organization...' : 'Authenticating...'}
      </p>
    </div>
  )
}

Authenticate.layout = (page: React.ReactNode) => <MarketingLayout>{page}</MarketingLayout>

export default Authenticate
